/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable react/jsx-no-bind */
import React, { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import { uploadBatch } from '@actions/batch-actions';
import { fetchLayerConfig } from '@actions/layers-actions';
import { colors, dotmapsGray } from '@constants/colors';
import { dialogStyles } from '@constants/mui-theme';
import CircularProgress from '@material-ui/core/CircularProgress';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { Icon, IconButton, Select } from '@mui';
import Dialog from '@shared/dialogs/dialog';
import DialogActions from '@shared/dialogs/dialog-actions';
import FileArea from '@shared/files/file-area';
import { canAccessEntity } from '@utils/permission-utils';
import { sortByField } from '@utils/data-types-utils';
import './batch-upload-form.scss';

const { MenuItem } = Select;

const defaultState = {
  layerType: null,
  entityType: null,
  typeSelected: null,
  toUpload: null,
  uploadStarted: false,
  progress: 0,
  fileValidationError: false,
  uploadErrored: false
};

// Restrict file uploads to 8 mb:
const MAX_UPLOAD_SIZE = 8388608;

const BatchUploadForm = ({ open: controlledOpen = null, onClose }) => {
  const dispatch = useDispatch();

  // Redux state
  const city = useSelector(state => state.config.city);
  const entityTypes = useSelector(state => state.dataTypes.map_type);
  const layers = useSelector(state => state.layers);

  // Local state
  const [layerType, setLayerType] = useState(defaultState.layerType);
  const [entityType, setEntityType] = useState(defaultState.entityType);
  const [typeSelected, setTypeSelected] = useState(defaultState.typeSelected);
  const [toUpload, setToUpload] = useState(defaultState.toUpload);
  const [uploadStarted, setUploadStarted] = useState(defaultState.uploadStarted);
  const [, setProgress] = useState(defaultState.progress);
  const [fileValidationError, setFileValidationError] = useState(defaultState.fileValidationError);
  const [uploadErrored, setUploadErrored] = useState(defaultState.uploadErrored);
  const [open, setOpen] = useState(controlledOpen === null ? false : controlledOpen);

  const prevControlledOpenRef = useRef(controlledOpen);

  const resetState = useCallback(() => {
    setLayerType(defaultState.layerType);
    setEntityType(defaultState.entityType);
    setTypeSelected(defaultState.typeSelected);
    setToUpload(defaultState.toUpload);
    setUploadStarted(defaultState.uploadStarted);
    setProgress(defaultState.progress);
    setFileValidationError(defaultState.fileValidationError);
    setUploadErrored(defaultState.uploadErrored);
  }, []);

  const handleOpen = useCallback(() => {
    resetState();
    setOpen(true);
  }, [resetState]);

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
    setOpen(false);
  }, [onClose]);

  // Fetch layer config on mount if empty
  useEffect(() => {
    if (R.isEmpty(layers)) {
      dispatch(fetchLayerConfig());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle controlled open prop changes
  useEffect(() => {
    if (controlledOpen === null) {
      return;
    }
    if (controlledOpen !== prevControlledOpenRef.current) {
      if (controlledOpen === true && !open) {
        handleOpen();
      }
      if (controlledOpen === false && open) {
        handleClose();
      }
    }
    prevControlledOpenRef.current = controlledOpen;
  }, [controlledOpen, open, handleOpen, handleClose]);

  const handleProgress = useCallback(event => {
    const newProgress = Math.floor((event.loaded / event.total) * 100);
    setProgress(newProgress);
    if (newProgress === 100) {
      setUploadStarted(false);
      setOpen(false);
    }
  }, []);

  const handleFail = useCallback(() => {
    setUploadErrored(true);
  }, []);

  const handleUploadClick = useCallback(() => {
    if (toUpload) {
      setUploadStarted(true);
      const upload = {
        active: true,
        file: toUpload,
        entityType,
        layerType,
        status: 0
      };
      dispatch(uploadBatch(upload, handleProgress, handleFail));
    }
  }, [toUpload, entityType, layerType, dispatch, handleProgress, handleFail]);

  const handleFileClear = useCallback(() => {
    setFileValidationError(false);
    setToUpload(null);
  }, []);

  const handleFileChange = useCallback(file => {
    if (file) {
      setToUpload(file);
      setFileValidationError(file.size > MAX_UPLOAD_SIZE);
    } else if (toUpload) {
      setToUpload(null);
    }
  }, [toUpload]);

  const onLayerChange = useCallback(event => {
    setLayerType(event.target.value);
  }, []);

  const onTypeChange = useCallback(event => {
    setEntityType(event.target.value);
    setTypeSelected(event.target.value);
  }, []);

  const getLayers = useCallback(() => {
    const layerList = [];
    for (const name in layers) {
      if (Object.prototype.hasOwnProperty.call(layers, name)) {
        // For Seattle: Don't list Zoning and Streets Use Permits.
        // For other cities: Show all layers.
        if (city !== 'seattle' || (name !== 'zoning' && name !== 'street_use_permits')) {
          const layer = layers[name];
          layerList.push({
            label: layer.label,
            name: layer.name,
            // For Seattle: Disabled all layers but Hubs.
            // For other cities: Enable all layers.
            disabled: city === 'seattle' && layer.name !== 'hubs'
          });
        }
      }
    }
    return layerList;
  }, [city, layers]);

  const getEntityTypes = useCallback(() => {
    const entityList = sortByField('order')(R.values(R.filter(et => et.order > 0, entityTypes)));
    const list = [
      ...(Object.keys(entityList).map(key => (
        { name: entityList[key].name, label: entityList[key].label }
      ))),
      {
        name: 'layer',
        label: 'City Layer'
      }
    ];
    return list;
  }, [entityTypes]);

  const entityTypeRenderer = useCallback((selected, values) => {
    if (values?.length === 0 || !selected) {
      return 'Select';
    }
    return getEntityTypes().filter(et => et.name === selected)[0].label;
  }, [getEntityTypes]);

  const layerRenderer = useCallback((selected, values) => {
    if (values?.length === 0 || !selected) {
      return 'Select a layer';
    }
    return getLayers().find(layer => layer.name === selected).label;
  }, [getLayers]);

  if (!canAccessEntity(null, 'batchfile', 'add')) {
    return null;
  }

  let actions = null;
  if (open && !uploadStarted && !uploadErrored) {
    actions = (
      <DialogActions
        disabledSubmit={!toUpload || !typeSelected || fileValidationError}
        onCancel={handleClose}
        onSubmit={handleUploadClick}
        submitLabel="UPLOAD"
      />
    );
  }
  if (uploadErrored) {
    actions = (
      <DialogActions
        onCancel={handleClose}
        onSubmit={handleOpen}
        submitLabel="TRY AGAIN"
      />
    );
  }

  return (
    <Fragment>
      {controlledOpen === null &&
        <IconButton
          id="dataTableBatchUploadButton"
          onClick={handleOpen}
          customIcon={<Icon role="img" color={colors.neutral.white}>add</Icon>}
          tooltip="Batch Upload"
        >
          <Icon role="img" color={colors.neutral.white}>add</Icon>
        </IconButton>
      }
      <Dialog
        actions={actions}
        onClose={handleClose}
        open={open || false}
        title={open && !uploadStarted && !uploadErrored ? 'Batch Upload' : ''}
      >
        {!uploadStarted && !uploadErrored &&
          <div>
            <div styleName="subheader">
              Select the data you want to create or update in batches:
            </div>
            <div styleName="section section-select">
              <Select
                displayEmpty
                onChange={onTypeChange}
                renderValue={values => entityTypeRenderer(typeSelected, values)}
                value={typeSelected}
              >
                {getEntityTypes().map(et => (
                  <MenuItem
                    key={et.name}
                    value={et.name}
                  >
                    {et.label}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <div styleName="section section-select" style={{ display: typeSelected === 'layer' ? 'block' : 'none' }}>
              <Select
                disabled={typeSelected !== 'layer'}
                displayEmpty
                onChange={onLayerChange}
                renderValue={values => layerRenderer(layerType, values)}
                value={layerType}
              >
                {getLayers().map(layer => (
                  <MenuItem
                    disabled={layer.disabled}
                    key={layer.name}
                    value={layer.name}
                  >
                    {layer.label}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <div styleName="section">
              <div styleName="section-header">
                Choose a file:
              </div>
              <div styleName="section-row">
                <FileArea
                  id="batch-upload-input"
                  accept={typeSelected === 'layer' ? 'application/json,application/geo+json,.geojson' : 'text/csv'}
                  hasError={fileValidationError}
                  onFileClear={handleFileClear}
                  onFileSelect={handleFileChange}
                />
              </div>
            </div>
            {fileValidationError && toUpload &&
              <div styleName="footer file-upload-error">
                The file ({(toUpload.size / 1048576).toFixed(0)} MB) exceeds the limit.
              </div>
            }
            {!fileValidationError && (
              <div styleName="footer">
                Acceptable file type:&nbsp;
                {typeSelected !== 'layer' && 'CSV'}
                {typeSelected === 'layer' && 'JSON'}
                &nbsp;&nbsp;&bull;
                Max file size: 8 MB
              </div>
            )}
          </div>
        }
        {uploadStarted && !uploadErrored &&
          <div styleName="loading-container">
            <CircularProgress size={66} thickness={6} />
            <div style={dialogStyles.content.title}>
              Uploading...
            </div>
          </div>
        }
        {uploadErrored &&
          <div styleName="error-container">
            <ErrorOutlineIcon htmlColor={dotmapsGray} style={{ fontSize: '4.5rem' }} />
            <div style={dialogStyles.content.title}>
              Upload Failed
            </div>
          </div>
        }
      </Dialog>
    </Fragment>
  );
};

BatchUploadForm.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool
};

export default BatchUploadForm;
